import { Component, Input, OnInit } from '@angular/core';
import { Project } from '../../../model/project/project';
import ProductHelper from '../../../helpers/product-helper';
import Product from '../../../model/product/product';
import { FormatDecimal } from '../../../pipes/format-decimal';
import { FormatPhoneNumber } from '../../../modules/app-pipes/format-phonenumber';
import PlacementInvoice from '../../../model/invoice/placement-invoice';
import ProjectInvoice, {
  BoardPartPriceDisplay,
  ProductPriceDisplay,
} from '../../../model/invoice/project-invoice';
import { PriceLine } from '../../../model/price-line';
import ProjectHelper from '../../../helpers/project-helper';
import Windowsticker from '../../../model/product/windowsticker/windowsticker';
import { SubProject } from '../../../model/project/sub-project';
import Board from 'src/app/model/product/board/board';
import BoardPart from 'src/app/model/product/board/board-part';
import { CollectionHelper } from 'src/app/helpers/collection-helper';
import { ProjectDisplayObservableService } from 'src/app/services/project-display-observable.service';
import { StringHelper } from 'src/app/helpers/string-helper';
import { ProductDeletedService } from 'src/app/services/product-deleted-service';
import { Observable, Subject } from 'rxjs';
import { BoardPartAddedEvent } from 'src/app/events/board-part-added';
import { INVOICE_PRICE_DISPLAY_DEBOUNCE_TIME } from 'src/globals/globals';
import { SubscriptionWithDestroyTriggerComponent } from 'src/app/subscription-helpers/subscription-with-destroy-trigger.component';

@Component({
  selector: 'invoice-preview',
  templateUrl: './invoice-preview.component.html',
  styleUrls: ['./invoice-preview.component.scss'],
  providers: [FormatDecimal, FormatPhoneNumber],
})
export class InvoicePreviewComponent
  extends SubscriptionWithDestroyTriggerComponent
  implements OnInit
{
  @Input()
  project: Project;
  @Input()
  inputProject: SubProject;
  @Input()
  productAdded: Subject<Product>;
  @Input()
  boardPartAdded: Subject<BoardPartAddedEvent>;

  placementInvoices: PlacementInvoice[] = [];
  projectManagerName: string = '';

  recalculateBoardPartDisplaysSubscribable: Observable<any>;
  destroyThisComponent = new Subject<any>();

  constructor(
    private projectDisplayObservableService: ProjectDisplayObservableService,
    private productDeletedService: ProductDeletedService,
    private formatDecimal: FormatDecimal
  ) {
    super();
  }

  ngOnInit() {
    this.calculatePlacementInvoices();
    this.calculatePlacementInvoiceSubtotals();
    this.calculateProductPriceDisplays();
    this.calculateProjectManagerName();
    this.initializeSubscriptions();
  }

  initializeSubscriptions() {
    this.recalculateBoardPartDisplaysSubscribable =
      this.createObservableUntilDestroy(
        this.projectDisplayObservableService.getProductPlacementVariableChangedSubscribable(),
        this.projectDisplayObservableService.getProductMaterialChangedSubscribable(),
        this.projectDisplayObservableService.getPaintedPostsChangedSubscribable(),
        this.projectDisplayObservableService.getProductSizeChangedSubscribable(),
        this.projectDisplayObservableService.getProductPartTypeChangedSubscribable(),
        this.projectDisplayObservableService.getProductPartDeletedSubscribable()
      );
    const recalculateInvoiceDisplaysObservable =
      this.createObservableUntilDestroy(
        this.projectDisplayObservableService.getProjectInvoicesChangedSubscribable(),
        this.projectDisplayObservableService.getInvoiceSentSubscribable()
      );
    this.addSubscription(recalculateInvoiceDisplaysObservable, () => {
      this.calculatePlacementInvoices();
      this.calculatePlacementInvoiceSubtotals();
      this.calculateProductPriceDisplays();
    });
    this.addSubscription(
      this.projectDisplayObservableService.getProjectContactsChangedSubscribable(),
      () => {
        this.calculateProjectManagerName();
      }
    );
    this.addSubscription(
      this.productDeletedService.getProductDeletedSubscribable(),
      (id: number) => {
        this.deleteProductPrice(id);
      }
    );
    this.addSubscription(
      this.projectDisplayObservableService.getProjectInvoicePriceChangedSubscribable(),
      () => {
        this.calculatePlacementInvoiceSubtotals();
        this.calculateProductPriceDisplays();
      },
      () => {},
      INVOICE_PRICE_DISPLAY_DEBOUNCE_TIME
    );
    this.addSubscription(
      this.recalculateBoardPartDisplaysSubscribable,
      () => {
        this.calculateAllBoardPartDisplays();
        this.calculatePlacementInvoiceSubtotals();
      },
      () => {},
      INVOICE_PRICE_DISPLAY_DEBOUNCE_TIME
    );
    if (this.productAdded != null) {
      this.addSubscription(this.productAdded, (p: Product) => {
        this.addProductPrice(p);
        this.calculateAllBoardPartDisplays();
      });
    }
    if (this.boardPartAdded != null) {
      this.addSubscription(this.boardPartAdded, (bp: BoardPartAddedEvent) => {
        this.calculateAllBoardPartDisplays();
      });
    }
  }

  calculatePlacementInvoices() {
    this.placementInvoices = CollectionHelper.isPopulated(
      this.inputProject.projectInvoices
    )
      ? this.inputProject.projectInvoices.filter(
          (pi) => pi.type === 'PlacementInvoice'
        )
      : [];
  }

  calculatePlacementInvoiceSubtotals() {
    if (CollectionHelper.isPopulated(this.placementInvoices)) {
      this.placementInvoices.forEach((pi) => {
        pi.displaySubtotal = this.getInvoiceSubTotalNumber(pi);
      });
    }
  }

  calculateProductPriceDisplays() {
    if (
      CollectionHelper.isPopulated(this.placementInvoices) &&
      CollectionHelper.isPopulated(this.project.products)
    ) {
      this.placementInvoices.forEach((pi) => {
        if (pi.displayProductPrice == null) {
          pi.displayProductPrice = new Map<number, ProductPriceDisplay>();
        }
        if (pi.displayBoardPartPrice == null) {
          pi.displayBoardPartPrice = new Map<number, BoardPartPriceDisplay[]>();
        }
        this.project.products.forEach((p, i) => {
          pi.displayProductPrice.set(
            p.id,
            this.calculateProductPriceDisplay(p, pi)
          );
          if (p.type === 'Board') {
            this.calculateBoardPartDisplays(<Board>p, pi, i);
          }
        });
      });
    } else if (
      CollectionHelper.isPopulated(this.placementInvoices) &&
      !CollectionHelper.isPopulated(this.project.products)
    ) {
      this.placementInvoices.forEach((pi) => {
        if (pi.displayProductPrice != null) {
          pi.displayProductPrice.clear();
        }
        if (pi.displayBoardPartPrice != null) {
          pi.displayBoardPartPrice.clear();
        }
      });
    }
  }

  calculateProductPriceDisplay(
    product: Product,
    invoice: ProjectInvoice
  ): ProductPriceDisplay {
    const productTotal = this.getProductPrice(product, invoice);
    const priceLines = this.getInvoiceLinesProduct(product, invoice);
    return {
      productTotal: productTotal,
      prices: CollectionHelper.isPopulated(priceLines)
        ? priceLines.map((pl) => {
            return {
              reason: pl.text,
              price: this.formatPrice(Number(pl.price)),
            };
          })
        : [],
    };
  }

  calculateAllBoardPartDisplays() {
    if (
      CollectionHelper.isPopulated(this.placementInvoices) &&
      CollectionHelper.isPopulated(this.project.products)
    ) {
      this.placementInvoices.forEach((pi) => {
        this.project.products.forEach((p, i) => {
          if (p.type === 'Board') {
            this.calculateBoardPartDisplays(<Board>p, pi, i);
          }
        });
      });
    } else if (
      CollectionHelper.isPopulated(this.placementInvoices) &&
      !CollectionHelper.isPopulated(this.project.products)
    ) {
      this.placementInvoices.forEach((pi) => {
        if (pi.displayBoardPartPrice != null) {
          pi.displayBoardPartPrice.clear();
        }
      });
    }
  }

  calculateBoardPartDisplays(
    board: Board,
    invoice: ProjectInvoice,
    boardIndex: number
  ) {
    if (CollectionHelper.isPopulated(board.boardParts)) {
      if (invoice.displayBoardPartPrice == null) {
        invoice.displayBoardPartPrice = new Map<
          number,
          BoardPartPriceDisplay[]
        >();
      }
      board.boardParts.forEach((bp, i) => {
        invoice.displayBoardPartPrice.set(
          bp.id,
          this.getBoardPartPriceDisplays(board, boardIndex, bp, i, invoice)
        );
      });
    }
  }

  addProductPrice(p: Product) {
    if (CollectionHelper.isPopulated(this.placementInvoices)) {
      this.placementInvoices.forEach((pi) => {
        if (pi.displayProductPrice == null) {
          pi.displayProductPrice = new Map<number, ProductPriceDisplay>();
        }
        pi.displayProductPrice.set(
          p.id,
          this.calculateProductPriceDisplay(p, pi)
        );
      });
    }
  }

  deleteProductPrice(id: number) {
    if (CollectionHelper.isPopulated(this.placementInvoices)) {
      this.placementInvoices.forEach((pi) => {
        if (pi.displayProductPrice != null) {
          pi.displayProductPrice.delete(id);
        }
      });
    }
  }

  calculateProjectManagerName() {
    const pm = CollectionHelper.isPopulated(this.project.projectContacts)
      ? this.project.projectContacts.find((pc) => pc.projectManager)
      : null;
    this.projectManagerName =
      pm == null || pm.contact == null
        ? ''
        : StringHelper.join([pm.contact.firstName, pm.contact.lastName], ' ');
  }

  getProductPrice(product: Product, invoice?: ProjectInvoice) {
    return this.formatPrice(
      ProductHelper.getPrice(product, invoice, <Project>this.project)
    );
  }

  getType(product: Product) {
    return ProductHelper.getType(product.type);
  }

  getInvoiceLinesProduct(
    product: Product,
    invoice: ProjectInvoice
  ): PriceLine[] {
    return ProductHelper.getPriceLinesOfProductAndInvoiceInvoice(
      product.id,
      invoice
    );
  }

  formatPrice(price: number) {
    return this.formatDecimal.transform(price, true, 2);
  }

  getInvoiceSubTotalNumber(invoice: ProjectInvoice) {
    return ProjectHelper.getSubtotalOfInvoice(invoice, <Project>this.project);
  }

  getSize(product: Product) {
    if (product.productSize != null) {
      if (
        product.productSize.height != null &&
        product.productSize.width != null
      ) {
        return (
          '- ' +
          product.productSize.height +
          'cm x ' +
          product.productSize.width +
          'cm - ' +
          ProductHelper.getFormat(product.productSize)
        );
      }
    }
  }

  getBoardPartPriceDisplays(
    board: Board,
    boardIndex: number,
    part: BoardPart,
    partIndex: number,
    invoice: ProjectInvoice
  ): BoardPartPriceDisplay[] {
    return ProductHelper.getBoardPartPriceDisplays(
      board,
      boardIndex,
      this.project.products != null && this.project.products.length > 1,
      part,
      partIndex,
      invoice
    );
  }

  getTemporary(product: Product) {
    if (product != null) {
      let ws = <Windowsticker>product;
      return ProductHelper.getTemporary(ws.temporaryMaterial);
    }
  }
}
